import React from 'react';
import { Field, BaseFieldProps } from 'redux-form';
import { Control, useController } from 'react-hook-form';
import { connect } from 'react-redux';
import * as R from 'ramda';
import TextInput from '../text';
import { TextInput as TextInputControl } from '../text/text-input';
import Dropdown from '../../../dropdown';
import Icon from '../../../icon';
import { useAppSelector } from '@hooks/redux';
import { User } from '../../../../types/objects';
import './styles.scss';

type DialCode = {
  country_code: string;
  dial_code: string;
  icon: string;
  name: string;
  name_eng: string;
};

type DialCodeInputProps = {
  dialCodes: DialCode[];
};

type Props = DialCodeInputProps & BaseFieldProps & {
  placeholder?: string;
};

// @ts-expect-error
const mapStateToProps = (state) => ({
  dialCodes: state.language.dialCodes,
});

export const DialCodeInput = ({
  input: { value, onChange }, dialCodes,
}: {
  input: {
    value: string | null;
    onChange: (value: string) => void;
  };
  dialCodes: DialCode[];
}) => {
  const [search, setSearch] = React.useState<string | null>(null);

  const selectedOption = R.find(R.propEq('dial_code', value), dialCodes) as DialCode;

  const options = search
    ? dialCodes.filter((option: DialCode) => (
      option.name.toLowerCase().includes(search.toLowerCase())
      || option.name_eng.toLowerCase().includes(search.toLowerCase())
    ))
    : dialCodes;

  return (
    <Dropdown
      search
      className="dropdown"
      toggle={(
        <div className="PhoneNumberInput__Toggle" role="button">
          <Icon type="arrow_drop_down" />
          {selectedOption && (
            <>
              <img src={selectedOption.icon} alt={selectedOption.name} />
              <div>{selectedOption.dial_code}</div>
            </>
          )}
        </div>
      )}
      scrolling
    >
      <Dropdown.SearchInput debounce={false} filter={false} onSearch={setSearch} />
      {options.map((option) => (
        <Dropdown.Item
          selected={selectedOption?.dial_code === option.dial_code}
          key={option.country_code}
          onClick={() => onChange(option.dial_code)}
        >
          {option.name}
        </Dropdown.Item>
      ))}
    </Dropdown>
  );
};

const PhoneNumberInputField = ({ name, dialCodes, placeholder }: Props) => (
  <div className="PhoneNumberInput fs-mask">
    <Field
      name={`${name}.dial_code`}
      component={DialCodeInput}
      dialCodes={dialCodes}
    />
    <TextInput
      className="phoneInput"
      name={`${name}.number`}
      placeholder={placeholder}
      maxLength={12}
      // @ts-expect-error
      onKeyPress={(e: KeyboardEvent) => {
        if (Number.isNaN(parseInt(e.key))) return e.preventDefault();

        return true;
      }}
    />
  </div>
);

export const PhoneNumberInput = connect(mapStateToProps)(PhoneNumberInputField);

type PhoneNumberInputHookFormProps = {
  name: string;
  control: Control<any>;
  placeholder?: string;
};

export function PhoneNumberInputHookForm({
  name, control, placeholder,
}: PhoneNumberInputHookFormProps) {
  const dialCodes = useAppSelector((state) => state.language.dialCodes) as DialCode[];
  const dialCode = useController({
    name: `${name}.dial_code`,
    control,
  });
  const number = useController({
    name: `${name}.number`,
    control,
  });

  return (
    <div className="PhoneNumberInput fs-mask">
      <DialCodeInput
        input={{
          value: dialCode.field.value,
          onChange: dialCode.field.onChange,
        }}
        dialCodes={dialCodes || []}
      />
      <TextInputControl
        className="phoneInput"
        name={number.field.name}
        value={number.field.value ?? ''}
        onChange={number.field.onChange}
        onBlur={number.field.onBlur}
        ref={number.field.ref}
        placeholder={placeholder}
        maxLength={12}
        onKeyPress={(event) => {
          if (Number.isNaN(parseInt(event.key, 10))) {
            event.preventDefault();
            return false;
          }
          return true;
        }}
      />
    </div>
  );
}

const transformPhoneNumber = (number: string | null | undefined) => {
  if (!number) return '';
  if (number.substr(0, 1) === '0') return number.substr(1);

  return number;
};

// @ts-expect-error
PhoneNumberInput.initialize = (user: User) => ({
  dial_code: user.phone?.dial_code || '+31', // default to Dutch
  number: user.phone?.dial_code ? user.phone.number : transformPhoneNumber(user.phone?.number), // If no dial code try to remove 0 at the beginning
});
